import React, { useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Info, Search,
  List, Globe2, BookOpen, Settings, Sun, Moon,
} from 'lucide-react';
import { trackUserAction } from '../utils/analytics';
import { useShowSearchInNavigation } from '../utils/navigationPreferencesStore';
import { useSearchFeatureEnabled } from '../utils/searchFeaturePreferencesStore';
import { localePathPrefix } from '../utils/localeRouting';
import AuthForm from './auth/AuthForm';

function pathSearchActive(pathname) {
  if (pathname === '/s' || pathname.startsWith('/s/')) return true;
  return /^\/[a-z]{2}(?:-[A-Z]{2})?\/s(?:\/|$)/.test(pathname);
}

function pathLessonIndexActive(pathname) {
  if (pathname === '/topics') return true;
  return /^\/[a-z]{2}(?:-[A-Z]{2})?\/topics$/.test(pathname);
}

function pathWorldsTopicsActive(pathname) {
  if (pathname === '/topics/explorer' || pathname.startsWith('/topics/explorer')) return true;
  if (/^\/[a-z]{2}(?:-[A-Z]{2})?\/topics\/explorer/.test(pathname)) return true;
  if (pathname.startsWith('/topics/') && !pathname.includes('/lesson/')) {
    if (pathname === '/topics' || pathname.startsWith('/topics/catalog')) return false;
    return true;
  }
  if (/^\/[a-z]{2}(?:-[A-Z]{2})?\/topics\//.test(pathname) && !pathname.includes('/lesson/')) {
    if (/^\/[a-z]{2}(?:-[A-Z]{2})?\/topics$/.test(pathname)) return false;
    return true;
  }
  return false;
}

function pathAboutActive(pathname) {
  if (pathname === '/about') return true;
  return /^\/[a-z]{2}(?:-[A-Z]{2})?\/about$/.test(pathname);
}

function pathStoriesActive(pathname) {
  return pathname === '/stories' || pathname.startsWith('/stories/');
}

export default function Sidebar() {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const [showSearchInNav] = useShowSearchInNavigation();
  const [searchFeatureEnabled] = useSearchFeatureEnabled();
  const navigate = useNavigate();
  const [showReauthModal, setShowReauthModal] = useState(false);

  const navItems = useMemo(() => {
    const prefix = localePathPrefix(location.pathname);
    const items = [
      { to: '/', label: 'Home', icon: <Home size={22} strokeWidth={2.2} aria-hidden />, isActive: (p) => p === '/' },
    ];
    if (searchFeatureEnabled && showSearchInNav) {
      items.push({
        to: `${prefix}/s`,
        label: 'Search',
        title: 'Web search',
        icon: <Search size={22} strokeWidth={2.2} aria-hidden />,
        isActive: pathSearchActive,
      });
    }
    items.push(
      { to: `${prefix}/topics`,          label: 'Lessons', title: 'All lessons',         icon: <List     size={22} strokeWidth={2.2} aria-hidden />, isActive: pathLessonIndexActive },
      { to: `${prefix}/topics/explorer`, label: 'Worlds',  title: 'Pick a world',        icon: <Globe2   size={22} strokeWidth={2.2} aria-hidden />, isActive: pathWorldsTopicsActive },
      { to: '/stories',                  label: 'Stories', title: 'Kids Place Stories',   icon: <BookOpen size={22} strokeWidth={2.2} aria-hidden />, isActive: pathStoriesActive },
      { to: `${prefix}/about`,           label: 'About',                                  icon: <Info     size={22} strokeWidth={2.2} aria-hidden />, isActive: pathAboutActive },
    );
    return items;
  }, [location.pathname, showSearchInNav, searchFeatureEnabled]);

  const openSettingsAuth = () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setShowReauthModal(true);
  };

  const handleReauthSuccess = () => {
    sessionStorage.setItem('explore_settings_reauth', String(Date.now()));
    setShowReauthModal(false);
    trackUserAction.navigationClick('settings');
    navigate('/settings');
  };

  const tabBase = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    padding: '8px 2px',
    textDecoration: 'none',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    minWidth: 0,
    fontSize: 10,
    fontWeight: 500,
  };

  return (
    <>
      {/* ── Parent verification modal ─────────────────────────────────────── */}
      {showReauthModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' }}
          onClick={() => setShowReauthModal(false)}
        >
          <div
            style={{ background: 'var(--color-bg-card)', borderRadius: 22, padding: '36px 28px 28px', maxWidth: 380, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.45)', position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowReauthModal(false)}
              aria-label="Close"
              style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text)', opacity: 0.45, fontSize: 18, lineHeight: 1, padding: 4 }}
            >
              ✕
            </button>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 44, marginBottom: 10, lineHeight: 1 }}>🔒</div>
              <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 800, color: 'var(--color-text)', letterSpacing: '-0.02em' }}>
                Parent verification
              </h2>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text)', opacity: 0.6, lineHeight: 1.5 }}>
                Sign in to confirm it's you. This keeps Settings out of reach of children.
              </p>
            </div>
            <AuthForm compact onSuccess={handleReauthSuccess} />
          </div>
        </div>
      )}

      {/* ── Bottom tab bar ────────────────────────────────────────────────── */}
      <nav
        role="navigation"
        aria-label="Main navigation"
        style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(100vw, 480px)',
          background: 'var(--color-bg-card)',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'stretch',
          zIndex: 1000,
          boxShadow: '0 -2px 8px var(--color-shadow)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {navItems.map((item) => {
          const active = item.isActive(location.pathname);
          return (
            <Link
              key={item.to}
              to={item.to}
              title={item.title || item.label}
              onClick={() => trackUserAction.navigationClick(item.label.toLowerCase())}
              style={{ ...tabBase, color: active ? 'var(--color-primary)' : 'var(--color-secondary)' }}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={openSettingsAuth}
          title="Settings (parent verification required)"
          style={{ ...tabBase, color: location.pathname === '/settings' ? 'var(--color-primary)' : 'var(--color-secondary)' }}
        >
          <Settings size={22} strokeWidth={2.2} aria-hidden />
          <span>Settings</span>
        </button>

        <button
          type="button"
          onClick={() => { trackUserAction.featureUsed('theme_toggle'); toggleTheme(); }}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ ...tabBase, color: 'var(--color-secondary)' }}
        >
          {theme === 'dark'
            ? <Sun size={22} strokeWidth={2.2} aria-hidden />
            : <Moon size={22} strokeWidth={2.2} aria-hidden />}
          <span>Theme</span>
        </button>
      </nav>
    </>
  );
}
